import useEmblaCarousel from 'embla-carousel-react'
import * as React from 'react'

import { cn } from '~/styled'

import { CarouselArrowNext, CarouselArrowPrevious } from './CarouselArrows'
import { CarouselPagination } from './CarouselPagination'
import { CarouselSlide } from './CarouselSlide'
import { CarouselSlider } from './CarouselSlider'

type EmblaApi = ReturnType<typeof useEmblaCarousel>[1]

type CarouselContextValue = {
  emblaRef: React.RefCallback<HTMLDivElement>
  emblaApi: EmblaApi
  selectedIndex: number
  scrollSnaps: number[]
  canScrollPrev: boolean
  canScrollNext: boolean
  scrollPrev: () => void
  scrollNext: () => void
  scrollTo: (index: number) => void
}

const CarouselContext = React.createContext<CarouselContextValue | null>(null)

type CarouselProps = {
  currentSlide?: number
}

export const CarouselComponent = ({
  children,
  currentSlide = 0,
  ...props
}: React.PropsWithChildren<
  CarouselProps & React.HTMLAttributes<HTMLDivElement>
>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: currentSlide,
    align: 'center'
  })

  const [selectedIndex, setSelectedIndex] = React.useState(currentSlide)
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([])
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const scrollPrev = React.useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const scrollTo = React.useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index)
    },
    [emblaApi]
  )

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setCanScrollPrev(emblaApi.canScrollPrev())
    setCanScrollNext(emblaApi.canScrollNext())
  }, [emblaApi])

  const onInit = React.useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()

    emblaApi.on('reInit', onInit)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('reInit', onInit)
      emblaApi.off('reInit', onSelect)
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  const contextValue = React.useMemo<CarouselContextValue>(
    () => ({
      emblaRef,
      emblaApi,
      selectedIndex,
      scrollSnaps,
      canScrollPrev,
      canScrollNext,
      scrollPrev,
      scrollNext,
      scrollTo
    }),
    [
      emblaRef,
      emblaApi,
      selectedIndex,
      scrollSnaps,
      canScrollPrev,
      canScrollNext,
      scrollPrev,
      scrollNext,
      scrollTo
    ]
  )

  const { className, ...rest } = props

  return (
    <div className={cn('relative', 'w-full', className)} {...rest}>
      <CarouselContext.Provider value={contextValue}>
        {children}
      </CarouselContext.Provider>
    </div>
  )
}

export const useCarousel = () => {
  const context = React.useContext(CarouselContext)
  if (!context) {
    throw new Error('useCarousel must be used within a Carousel')
  }

  return {
    ...context,
    // Alias for backward compatibility
    currentSlide: context.selectedIndex
  }
}

export const Carousel = Object.assign(CarouselComponent, {
  ArrowNext: CarouselArrowNext,
  ArrowPrevious: CarouselArrowPrevious,
  Pagination: CarouselPagination,
  Slide: CarouselSlide,
  Slider: CarouselSlider
})

CarouselComponent.displayName = 'Carousel'
